/**
 * Custom code to ensure all selling plans are shown for every selected variant.
 *
 * This script patches the BS_PRODUCT object to add missing selling plan allocations
 * for variants that don't have allocations for all selling plans in a group.
 *
 * When a variant doesn't have an allocation for a selling plan, we copy the allocation
 * (with its original price) from another variant that has it, preserving the original prices.
 *
 * IMPORTANT: This script must be loaded BEFORE the widget scripts in your theme.
 * Place it in your theme's <head> or before the widget script tags.
 */

;(function () {
  'use strict'

  // Track if we've already set up the proxy/interceptor
  let isIntercepted = false
  let patchedProducts = new WeakSet()

  // Track which variant originally had each selling plan allocation
  // Map: selling_plan_id -> variant_id (the variant that originally had this allocation)
  const originalAllocationVariants = new Map()

  // Flag to prevent circular updates when we're programmatically changing variant
  let isUpdatingVariantFromSellingPlan = false

  // Cooldown: ignore *same* selling-plan re-dispatches for this many ms after we've updated the variant
  // (stops loop; we still process user clicking a *different* plan)
  const VARIANT_UPDATE_COOLDOWN_MS = 400
  let lastVariantUpdateAt = 0
  let lastSellingPlanWeSet = null

  /**
   * Deep clone an allocation object to avoid reference issues
   */
  function cloneAllocation(allocation) {
    return {
      price_adjustments: allocation.price_adjustments
        ? allocation.price_adjustments.map((adj) => ({ ...adj }))
        : [],
      price: allocation.price,
      compare_at_price: allocation.compare_at_price,
      per_delivery_price: allocation.per_delivery_price,
      selling_plan_id: allocation.selling_plan_id,
      selling_plan_group_id: allocation.selling_plan_group_id,
    }
  }

  /**
   * Ensure all selling plans in a group are allocated to all variants
   * This function mutates the product object in place.
   * It reuses existing allocation prices instead of calculating new ones.
   */
  function ensureAllSellingPlansShown(product) {
    if (!product || !product.selling_plan_groups || !product.variants) {
      return false
    }

    // Skip if already patched
    if (patchedProducts.has(product)) {
      return true
    }

    let wasModified = false

    // Process each selling plan group
    product.selling_plan_groups.forEach((group) => {
      // Only process Bony Subscriptions groups
      if (group.app_id !== 'Bony Subscriptions') {
        return
      }

      // First, collect all existing allocations for each selling plan from all variants
      // This creates a map: selling_plan_id -> allocation (from any variant that has it)
      // Also track which variant originally had each allocation
      const allocationTemplates = new Map()

      product.variants.forEach((variant) => {
        if (variant.selling_plan_allocations) {
          variant.selling_plan_allocations.forEach((allocation) => {
            // Only store allocations for plans in this group
            if (
              allocation.selling_plan_group_id === group.id &&
              !allocationTemplates.has(allocation.selling_plan_id)
            ) {
              allocationTemplates.set(allocation.selling_plan_id, allocation)
              // Track which variant originally had this allocation
              originalAllocationVariants.set(allocation.selling_plan_id, variant.id.toString())
            }
          })
        }
      })

      // Now ensure every variant has allocations for all selling plans in the group
      product.variants.forEach((variant) => {
        if (!variant.selling_plan_allocations) {
          variant.selling_plan_allocations = []
        }

        // Check each selling plan in the group
        group.selling_plans.forEach((plan) => {
          // Check if this variant already has an allocation for this plan
          const existingAllocation = variant.selling_plan_allocations.find(
            (allocation) => allocation.selling_plan_id === plan.id,
          )

          // If no allocation exists, copy one from another variant (if available)
          if (!existingAllocation) {
            const templateAllocation = allocationTemplates.get(plan.id)

            if (templateAllocation) {
              wasModified = true
              // Clone the allocation to avoid reference issues
              const clonedAllocation = cloneAllocation(templateAllocation)
              variant.selling_plan_allocations.push(clonedAllocation)
            }
            // If no template exists (no variant has this plan), skip it
            // This shouldn't happen in normal cases, but we handle it gracefully
          }
        })
      })
    })

    if (wasModified) {
      patchedProducts.add(product)
    }

    return wasModified
  }

  /**
   * Set up interception to patch BS_PRODUCT whenever it's set or accessed
   */
  function setupBSProductInterceptor() {
    if (isIntercepted) return

    // Patch immediately if BS_PRODUCT already exists
    if (window.BS_PRODUCT && typeof window.BS_PRODUCT === 'object') {
      ensureAllSellingPlansShown(window.BS_PRODUCT)
    }

    // Try to intercept future assignments using defineProperty
    try {
      const descriptor = Object.getOwnPropertyDescriptor(window, 'BS_PRODUCT')

      // If BS_PRODUCT is already defined with a getter/setter, we need to wrap it
      if (descriptor && (descriptor.get || descriptor.set)) {
        const originalGetter = descriptor.get
        const originalSetter = descriptor.set

        Object.defineProperty(window, 'BS_PRODUCT', {
          get: function () {
            const product = originalGetter ? originalGetter.call(window) : undefined
            if (product && typeof product === 'object') {
              ensureAllSellingPlansShown(product)
            }
            return product
          },
          set: function (newProduct) {
            if (newProduct && typeof newProduct === 'object') {
              ensureAllSellingPlansShown(newProduct)
            }
            if (originalSetter) {
              originalSetter.call(window, newProduct)
            }
          },
          configurable: true,
          enumerable: true,
        })
      } else {
        // BS_PRODUCT is a regular property (or doesn't exist yet), wrap it
        // Store the current value in a closure to avoid recursion
        let internalProduct = descriptor ? descriptor.value : undefined

        Object.defineProperty(window, 'BS_PRODUCT', {
          get: function () {
            if (internalProduct && typeof internalProduct === 'object') {
              ensureAllSellingPlansShown(internalProduct)
            }
            return internalProduct
          },
          set: function (newProduct) {
            if (newProduct && typeof newProduct === 'object') {
              ensureAllSellingPlansShown(newProduct)
            }
            internalProduct = newProduct
          },
          configurable: true,
          enumerable: true,
        })
      }

      isIntercepted = true
    } catch (e) {
      // If defineProperty fails (e.g., property is not configurable), fall back to polling
      // This is expected in some environments, so we don't log it as an error
      isIntercepted = false
    }
  }

  /**
   * Polling fallback - checks and patches BS_PRODUCT periodically
   */
  function setupPollingFallback() {
    const checkAndPatch = () => {
      if (window.BS_PRODUCT && typeof window.BS_PRODUCT === 'object') {
        ensureAllSellingPlansShown(window.BS_PRODUCT)
      }
    }

    // Check immediately
    checkAndPatch()

    // Check periodically (more frequent initially, then less frequent)
    let checkCount = 0
    const maxChecks = 300 // 30 seconds at 100ms intervals

    const intervalId = setInterval(() => {
      checkAndPatch()
      checkCount++
      if (checkCount >= maxChecks) {
        clearInterval(intervalId)
      }
    }, 100)

    // Check on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        checkAndPatch()
        // Reset counter on DOMContentLoaded to keep checking
        checkCount = 0
      })
    }

    // Check on window load as well
    window.addEventListener('load', checkAndPatch)
  }

  /**
   * Find a variant that has the given selling plan allocation
   * Returns the variant ID, or null if not found
   * Prefers the variant that originally had this allocation
   */
  function findVariantWithSellingPlan(sellingPlanId) {
    const product = window.BS_PRODUCT
    if (!product || !product.variants || !sellingPlanId) {
      return null
    }

    const planIdNum = Number(sellingPlanId)

    // First, try to find the variant that originally had this allocation
    const originalVariantId = originalAllocationVariants.get(planIdNum)
    if (originalVariantId) {
      // Verify the variant still exists
      const variant = product.variants.find((v) => v.id.toString() === originalVariantId)
      if (variant) {
        return originalVariantId
      }
    }

    // Fallback: find any variant that has this allocation
    for (const variant of product.variants) {
      if (variant.selling_plan_allocations) {
        const allocation = variant.selling_plan_allocations.find(
          (alloc) => alloc.selling_plan_id === planIdNum,
        )
        if (allocation) {
          return variant.id.toString()
        }
      }
    }

    return null
  }

  /**
   * Get the current variant ID from the form
   */
  function getCurrentVariantId() {
    // Try to find variant input in add-to-cart form first
    const form = document.querySelector('form[action*="/cart/add"]')
    if (form) {
      const variantInput =
        form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]')
      if (variantInput && variantInput.value) {
        return variantInput.value
      }
    }

    // Fallback: search globally for variant input
    const variantInput =
      document.querySelector('input[name="id"]') || document.querySelector('select[name="id"]')
    if (variantInput && variantInput.value) {
      return variantInput.value
    }

    return null
  }

  /**
   * Get the last variant ID from BS_PRODUCT
   */
  function getLastVariantId() {
    const product = window.BS_PRODUCT
    if (!product || !product.variants || product.variants.length === 0) {
      return null
    }
    const last = product.variants[product.variants.length - 1]
    return last && last.id != null ? last.id.toString() : null
  }

  /**
   * Get the variant input element
   */
  function getVariantInput() {
    // Try to find variant input in add-to-cart form first
    const form = document.querySelector('form[action*="/cart/add"]')
    if (form) {
      const variantInput =
        form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]')
      if (variantInput) {
        return variantInput
      }
    }

    // Fallback: search globally for variant input
    return document.querySelector('input[name="id"]') || document.querySelector('select[name="id"]')
  }

  /**
   * Update the variant input when a selling plan is selected
   */
  function updateVariantForSellingPlan(sellingPlanId) {
    if (isUpdatingVariantFromSellingPlan) {
      return
    }

    // Ignore only the "echo" of the plan we just set (widget re-dispatches same plan after we change variant)
    let variantId = null

    // One-time purchase (no selling plan): always use the last variant
    if (!sellingPlanId) {
      variantId = getLastVariantId()
    } else {
      const now = Date.now()
      const samePlanAsLast =
        lastSellingPlanWeSet !== null && String(lastSellingPlanWeSet) === String(sellingPlanId)
      const withinCooldown = now - lastVariantUpdateAt < VARIANT_UPDATE_COOLDOWN_MS
      if (withinCooldown && samePlanAsLast) {
        return
      }

      variantId = findVariantWithSellingPlan(sellingPlanId)
    }

    if (!variantId) {
      return
    }

    // Check if the current variant is already the target variant
    const currentVariantId = getCurrentVariantId()
    if (currentVariantId === variantId) {
      return
    }

    // Find the variant input element
    const variantInput = getVariantInput()
    if (!variantInput) {
      console.warn('[BS Custom Code] Could not find variant input element')
      return
    }

    // Update the variant
    isUpdatingVariantFromSellingPlan = true
    lastVariantUpdateAt = Date.now()
    lastSellingPlanWeSet = sellingPlanId

    variantInput.value = variantId

    // Trigger change event to notify theme/widgets
    const changeEvent = new Event('change', { bubbles: true })
    variantInput.dispatchEvent(changeEvent)

    // Also trigger input event for compatibility
    const inputEvent = new Event('input', { bubbles: true })
    variantInput.dispatchEvent(inputEvent)

    // Dispatch custom variant change event that widgets listen to
    const variantChangeEvent = new CustomEvent('bs:variant:change', {
      detail: { variantId: variantId },
      bubbles: true,
    })
    window.dispatchEvent(variantChangeEvent)

    // Some themes use a specific event name, dispatch that too
    const shopifyVariantChangeEvent = new CustomEvent('variant:change', {
      detail: { variantId: variantId },
      bubbles: true,
    })
    window.dispatchEvent(shopifyVariantChangeEvent)

    // Reset flag and "last plan" after cooldown
    setTimeout(() => {
      isUpdatingVariantFromSellingPlan = false
      lastSellingPlanWeSet = null
    }, VARIANT_UPDATE_COOLDOWN_MS)
  }

  /**
   * Set up listener for selling plan selection changes
   */
  function setupSellingPlanChangeListener() {
    function handler(event) {
      const detail = event.detail || {}
      const sellingPlanId = detail.sellingPlanId
      updateVariantForSellingPlan(sellingPlanId || null)
    }

    // Legacy/custom event name
    window.addEventListener('bs:selected-selling-plan-changed', handler)
    // New event name used by widgets
    window.addEventListener('bs:selling-plan:change', handler)
  }

  // Try to set up interception first
  setupBSProductInterceptor()

  // Always set up polling as a reliable fallback
  setupPollingFallback()

  // Set up selling plan change listener
  setupSellingPlanChangeListener()

  // Expose a manual patch function for edge cases or manual triggering
  window.__BS_PATCH_PRODUCT__ = function () {
    if (window.BS_PRODUCT && typeof window.BS_PRODUCT === 'object') {
      patchedProducts.delete(window.BS_PRODUCT) // Force re-patch
      ensureAllSellingPlansShown(window.BS_PRODUCT)
      return true
    }
    return false
  }
})()
